import {
  type BuildOrderCandidateOptions,
  buildOrderCandidate,
  isSplitCandidate,
  type OrderCandidate,
} from './merchantOrderCandidate';
import {
  type MatchableTransaction,
  type MatchOrderCandidatesOptions,
  type MerchantOrderMatch,
  matchOrderCandidates,
} from './merchantOrderMatch';
import { type MerchantOrder, type MerchantOrderSkipReason, merchantOrderSkipReason } from './merchantOrdersTypes';

export type ProcessMerchantOrdersOptions = MatchOrderCandidatesOptions & BuildOrderCandidateOptions;

export type MerchantOrderSkip = {
  order: MerchantOrder;
  /** Present only when the order got far enough to produce a transaction. */
  candidate?: OrderCandidate;
  reason: MerchantOrderSkipReason;
};

export type MerchantOrderProcessorStats = {
  total: number;
  matched: number;
  skipped: Record<MerchantOrderSkipReason, number>;
};

export type ProcessMerchantOrdersResult = {
  matches: MerchantOrderMatch[];
  skipped: MerchantOrderSkip[];
  stats: MerchantOrderProcessorStats;
};

const emptySkipCounts = (): Record<MerchantOrderSkipReason, number> =>
  Object.values(merchantOrderSkipReason).reduce(
    (counts, reason) => {
      counts[reason] = 0;
      return counts;
    },
    {} as Record<MerchantOrderSkipReason, number>,
  );

const buildStats = (total: number, matched: number, skipped: MerchantOrderSkip[]): MerchantOrderProcessorStats => {
  const counts = emptySkipCounts();
  skipped.forEach(({ reason }) => {
    counts[reason] += 1;
  });
  return { total, matched, skipped: counts };
};

/**
 * A categorized transaction whose memo already leads with the
 * candidate's product name has already been processed (e.g. a retried delete re-scraped the
 * order). Splits are unaffected — they are re-written wholesale, not prepended to.
 */
const isAlreadyCategorized = (
  match: MerchantOrderMatch,
  transactionsById: Map<string | null | undefined, MatchableTransaction>,
): boolean => {
  const { memo } = match.candidate.transaction;
  if (isSplitCandidate(match.candidate) || !memo) {
    return false;
  }
  const existingMemo = transactionsById.get(match.transactionId)?.memo ?? '';
  return existingMemo.trim().startsWith(memo);
};

/**
 * Turns merchant orders into Simplifi transactions and pairs each with the register transaction it
 * corresponds to.
 *
 * Pure: orders and transactions come in as plain objects and nothing touches the store or the
 * network, so the whole pipeline is exercisable from fixtures alone.
 *
 * The per-reason counts in `stats` are the point of the first pass. "47 orders, 3 matched" says
 * nothing; "31 noCandidateTransaction" says the payee patterns or the date window are wrong, and
 * "9 ambiguousTransaction" says the uniqueness rule is too strict for real data.
 */
export const processMerchantOrders = (
  orders: MerchantOrder[],
  transactions: MatchableTransaction[],
  options: ProcessMerchantOrdersOptions = {},
): ProcessMerchantOrdersResult => {
  const candidates: OrderCandidate[] = [];
  const skipped: MerchantOrderSkip[] = [];

  orders.forEach((order) => {
    const result = buildOrderCandidate(order, options);
    if (result.ok) {
      candidates.push(result.candidate);
    } else {
      skipped.push({ order, reason: result.skipReason });
    }
  });

  const matchResult = matchOrderCandidates(candidates, transactions, options);
  matchResult.skipped.forEach(({ candidate, reason }) => {
    skipped.push({ order: candidate.order, candidate, reason });
  });

  const transactionsById = new Map(transactions.map((transaction) => [transaction.id, transaction]));
  const matches: MerchantOrderMatch[] = [];
  matchResult.matches.forEach((match) => {
    if (isAlreadyCategorized(match, transactionsById)) {
      skipped.push({
        order: match.candidate.order,
        candidate: match.candidate,
        reason: merchantOrderSkipReason.alreadyCategorized,
      });
    } else {
      matches.push(match);
    }
  });

  return {
    matches,
    skipped,
    stats: buildStats(orders.length, matches.length, skipped),
  };
};
